import type { ReactNode } from 'react';

interface Props {
  /**
   * Unlock action (subscribe / pay / tip). Omit on a preview surface (the chat
   * card) where the whole card handles the tap — the button stays inert and the
   * click bubbles to the card.
   */
  onClick?: () => void;
  /** `primary` = mint-green fill (subscribe / tip), `secondary` = white fill (pay). */
  variant?: 'primary' | 'secondary';
  /** Smaller pill for tight surfaces (the chat-bubble post card). */
  compact?: boolean;
  className?: string;
  children: ReactNode;
}

/**
 * A paywall unlock pill — 1:1 with legacy `UnlockButton` in
 * `flows/bio/wall/components/post/locked.tsx`. Presentational: it triggers the
 * `onClick` unlock flow (owned by {@link usePostUnlock}), styled as the
 * mint-green or white pill the design uses. On preview surfaces `onClick` is
 * omitted so the tap falls through to the host (the chat card opens the modal).
 */
export function UnlockButton({
  onClick,
  variant = 'primary',
  compact,
  className,
  children,
}: Props) {
  const tone =
    variant === 'primary'
      ? 'bg-brand text-content-on-brand'
      : 'bg-white text-black';
  const size = compact
    ? 'min-h-7 gap-1 px-2.5 text-[12px]'
    : 'min-h-9 gap-1.5 px-4 text-[14px]';
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex items-center justify-center rounded-md font-semibold shadow transition hover:brightness-95 ${size} ${tone} ${
        className ?? ''
      }`}
    >
      {children}
    </button>
  );
}
